import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { HiArrowRight } from "react-icons/hi";
import { MdShoppingCart, MdLocalOffer, MdCheck, MdDownload, MdClose, MdBook } from "react-icons/md";
import { FaSeedling } from "react-icons/fa";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateItems } from "../../hooks/useTranslate";

const UI_TEXT = {
  EN: {
    localLabel:   "Local Products",
    heroTitle:    "Tempe Koro and Vegetable Products",
    heroSubtitle: "High-quality tempe koro and vegetable products from local Acehnese farmers. Traditionally processed, rich in plant-based protein for a healthy family.",
    cartTitle:    "Shopping Cart",
    cartEmpty:    "Your cart is still empty",
    cartEmptySub: "Add your favorite tempe koro products",
    cartSelected: "products selected",
    totalLabel:   "Total Payment",
    checkoutBtn:  "Checkout Now",
    continueBtn:  "Continue Shopping",
    removeBtn:    "Remove",
    allProducts:  "All Products",
    gridTitle:    "Choose Your Favorite Product",
    noProduct:    "No products available yet.",
    cartBtn:      "Cart",
    viewBtn:      "Catalog",
    addedBtn:     "Added",
    perUnit:      "per",
    gram:         "gram",
    cartTrigger:  "Cart",
    viewTrigger:  "Catalog",
    viewCart:     "View Cart",
    noteTitle:    "Products directly from Acehnese farmers",
    noteSub:      "Every purchase supports the welfare of local farmers and Rumoh Pangan Aceh's food security program. Delivery throughout Indonesia.",
    catalogTitle: "Product Catalog",
    catalogSub:   "Tempe Nusa — Rumoh Pangan Aceh",
    downloadBtn:  "Download PDF",
    closeBtn:     "Close",
  },
  ID: {
    localLabel:   "Produk Lokal",
    heroTitle:    "Tempe Koro dan Produk Sayuran",
    heroSubtitle: "Produk tempe koro dan sayuran berkualitas tinggi dari petani lokal Aceh. Diolah secara tradisional, kaya protein nabati untuk keluarga sehat.",
    cartTitle:    "Keranjang Belanja",
    cartEmpty:    "Keranjang masih kosong",
    cartEmptySub: "Tambahkan produk tempe koro pilihan Anda",
    cartSelected: "produk dipilih",
    totalLabel:   "Total Pembayaran",
    checkoutBtn:  "Checkout Sekarang",
    continueBtn:  "Lanjut Belanja",
    removeBtn:    "Hapus",
    allProducts:  "Semua Produk",
    gridTitle:    "Pilih Produk Favorit Anda",
    noProduct:    "Belum ada produk tersedia.",
    cartBtn:      "Keranjang",
    viewBtn:      "Katalog",
    addedBtn:     "Ditambah",
    perUnit:      "per",
    gram:         "gram",
    cartTrigger:  "Keranjang",
    viewTrigger:  "Katalog",
    viewCart:     "Lihat Keranjang",
    noteTitle:    "Produk langsung dari petani Aceh",
    noteSub:      "Setiap pembelian mendukung kesejahteraan petani lokal dan program ketahanan pangan Rumoh Pangan Aceh. Pengiriman ke seluruh Indonesia.",
    catalogTitle: "Katalog Produk",
    catalogSub:   "Tempe Nusa — Rumoh Pangan Aceh",
    downloadBtn:  "Download PDF",
    closeBtn:     "Tutup",
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.08) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

const formatPrice = (price) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(price);

// ─── Cart Hook ─────────────────────────────────────────────────────────────────
const useCart = () => {
  const [cart, setCart] = useState([]);
  const add = (product) => {
    setCart((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...product, qty: 1 }];
    });
  };
  const remove = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const total = cart.reduce((s, i) => s + i.harga * i.qty, 0);
  const count = cart.reduce((s, i) => s + i.qty, 0);
  return { cart, add, remove, total, count };
};

// ─── Product Card ──────────────────────────────────────────────────────────────
const ProductCard = ({ product, index, inView, onAdd, added, tx }) => (
  <div
    className="bg-white border border-[#e8e0d0] flex flex-col group hover:border-[#c9862a]/50 hover:shadow-2xl transition-all duration-300 overflow-hidden"
    style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(32px)",
      transition: `opacity 0.6s ease ${index * 0.09}s, transform 0.6s ease ${index * 0.09}s, border-color 0.3s, box-shadow 0.3s`,
    }}
  >
    <div className="relative overflow-hidden h-56">
      <img
        src={product.url}
        alt={product.nama}
        className="w-full h-full object-cover brightness-90 group-hover:brightness-100 group-hover:scale-105 transition-all duration-700"
      />
      <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#c9862a] group-hover:w-full transition-all duration-500" />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-serif text-lg font-bold text-[#1a1a18] leading-snug mb-2 group-hover:text-[#1a4a35] transition-colors duration-300">
        {product.nama}
      </h3>
      <span className="text-[#a09880] text-xs font-medium mb-3">{product.unit} {tx.gram}</span>
      <div className="w-6 h-[2px] bg-[#c9862a]/40 mb-4 group-hover:w-12 transition-all duration-300" />
      <p className="text-[#6b6456] text-sm leading-relaxed line-clamp-3 flex-1">{product.deskripsi}</p>
      <div className="mt-5 pt-5 border-t border-[#e8e0d0] flex items-center justify-between gap-3">
        <div>
          <div className="text-[#1a4a35] font-serif text-xl font-bold leading-tight">
            {formatPrice(product.harga)}
          </div>
          <div className="text-[#a09880] text-xs mt-0.5">{tx.perUnit} {product.unit} {tx.gram}</div>
        </div>
        <button
          onClick={() => onAdd(product)}
          className={`flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-4 py-2.5 transition-all duration-200 flex-shrink-0 ${
            added
              ? "bg-[#1a4a35] text-white border border-[#1a4a35]"
              : "border border-[#c9862a] text-[#1a1a18] hover:bg-[#c9862a] hover:text-white"
          }`}
        >
          {added ? <><MdCheck size={13} />{tx.addedBtn}</> : <><MdShoppingCart size={13} />{tx.cartBtn}</>}
        </button>
      </div>
    </div>
  </div>
);

// ─── Flipbook Modal ────────────────────────────────────────────────────────────
const FlipbookModal = ({ onClose, tx }) => {
  // Lock body scroll saat modal terbuka
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    // Overlay — fixed, full viewport, z-[9999] agar di atas navbar
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: "rgba(10, 10, 10, 0.88)" }}
    >
      {/* ── Top Bar ── */}
      <div className="flex-shrink-0 bg-[#1a4a35] flex items-center justify-between px-6 py-3 border-b border-[#c9862a]/30">
        {/* Kiri: ikon + judul */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#c9862a]/20 flex items-center justify-center">
            <MdBook className="text-[#c9862a]" size={18} />
          </div>
          <div>
            <p className="text-white font-bold text-sm tracking-widest uppercase leading-none">{tx.catalogTitle}</p>
            <p className="text-white/40 text-xs mt-0.5">{tx.catalogSub}</p>
          </div>
        </div>

        {/* Kanan: Download + Close */}
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex items-center gap-2 border border-white/20 hover:border-white/50 text-white/60 hover:text-white text-xs font-bold tracking-widest uppercase px-4 py-2.5 transition-all duration-200"
          >
            <MdClose size={15} />
            {tx.closeBtn}
          </button>
        </div>
      </div>

      {/* ── Flipbook Area ── */}
      <div className="flex-1 overflow-hidden relative">
        {/* Subtle accent lines kiri-kanan */}
        <div className="absolute left-0 top-0 h-full w-1 bg-[#c9862a]/40 z-10" />
        <div className="absolute right-0 top-0 h-full w-1 bg-[#c9862a]/40 z-10" />

        <iframe
          src="https://flipbook.so/embed/pCCe48Swi6BLKI3TQdtc"
          width="100%"
          height="100%"
          frameBorder="0"
          allowFullScreen
          className="block w-full h-full"
          title="Katalog Produk Tempe Nusa"
        />
      </div>

      {/* ── Bottom Bar ── */}
      <div className="flex-shrink-0 bg-[#111] border-t border-white/10 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaSeedling className="text-[#c9862a]" size={13} />
          <span className="text-white/40 text-xs">Rumoh Pangan Aceh — Produk langsung dari petani lokal</span>
        </div>
        <button
          onClick={onClose}
          className="text-white/30 hover:text-white/70 text-xs transition-colors tracking-widest uppercase"
        >
          × {tx.closeBtn}
        </button>
      </div>
    </div>
  );
};

// ─── Cart Drawer ───────────────────────────────────────────────────────────────
const WA_NUMBER = "6282315674892";

const buildWhatsAppLink = (cart, formatPrice) => {
  const itemsText = cart
    .map((item) => {
      const priceRaw = formatPrice(item.harga * item.qty)
        .replace("Rp", "").replace(/ /g, "").replace(/[.\s]/g, "");
      return `${item.nama} ${item.qty} harga ${priceRaw}`;
    })
    .join(", ");
  const totalRaw = formatPrice(cart.reduce((s, i) => s + i.harga * i.qty, 0))
    .replace("Rp", "").replace(/ /g, "").replace(/[.\s]/g, "");
  const message = `Halo, saya ingin pesan ${itemsText} dengan total harga ${totalRaw}. Apakah bisa?`;
  return `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${encodeURIComponent(message)}`;
};

const CartDrawer = ({ cart, onRemove, total, onClose, onCheckout, tx }) => {
  const handleCheckout = () => {
    const waLink = buildWhatsAppLink(cart, formatPrice);
    window.open(waLink, "_blank");
    if (onCheckout) onCheckout();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[#1a1a18]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
        <div className="bg-[#1a4a35] px-6 py-5 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MdShoppingCart className="text-[#c9862a]" size={18} />
              <span className="text-white font-bold text-base">{tx.cartTitle}</span>
            </div>
            <p className="text-white/50 text-xs mt-0.5">{cart.length} {tx.cartSelected}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none transition-colors">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <FaSeedling className="text-[#e8e0d0] text-5xl mb-4" />
              <p className="text-[#a09880] text-sm">{tx.cartEmpty}</p>
              <p className="text-[#c9a870]/60 text-xs mt-1">{tx.cartEmptySub}</p>
            </div>
          ) : (
            cart.map((item) => (
              <div key={item.id} className="flex gap-4 border border-[#e8e0d0] p-4 hover:border-[#c9862a]/30 transition-colors">
                <img src={item.url} alt={item.nama} className="w-16 h-16 object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-serif text-sm font-bold text-[#1a1a18] leading-snug truncate">{item.nama}</h4>
                  <p className="text-[#a09880] text-xs mt-0.5">{item.unit} {tx.gram}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[#1a4a35] font-bold text-sm">{formatPrice(item.harga * item.qty)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[#6b6456] text-xs">x{item.qty}</span>
                      <button onClick={() => onRemove(item.id)} className="text-[#a09880] hover:text-red-500 text-xs transition-colors">
                        {tx.removeBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <div className="border-t border-[#e8e0d0] px-6 py-5 bg-[#f7f3ec]">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-[#e8e0d0]" />
              <div className="w-1.5 h-1.5 bg-[#c9862a] rotate-45" />
              <div className="flex-1 h-px bg-[#e8e0d0]" />
            </div>
            <div className="flex items-center justify-between mb-5">
              <span className="text-[#6b6456] text-sm font-medium">{tx.totalLabel}</span>
              <span className="font-serif text-xl font-bold text-[#1a4a35]">{formatPrice(total)}</span>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full flex items-center justify-center gap-3 bg-[#c9862a] hover:bg-[#e09b3a] text-[#1a1a18] font-bold text-sm tracking-widest uppercase py-4 transition-colors duration-200 group"
            >
              {tx.checkoutBtn}
              <HiArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button onClick={onClose} className="w-full mt-3 text-[#6b6456] text-xs font-medium hover:text-[#1a4a35] transition-colors text-center py-2">
              {tx.continueBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Section ──────────────────────────────────────────────────────────────
const EtalaseSection = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const [rawProducts, setRawProducts] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const ref = useRef(null);
  const inView = useInView(ref);
  const [loaded, setLoaded] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [addedIds, setAddedIds] = useState([]);
  const { cart, add, remove, total, count } = useCart();

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const fetchProduk = async () => {
      try {
        const apiUrl = process.env.REACT_APP_URL_API;
        const res = await axios.get(`${apiUrl}/all-produk`);
        setRawProducts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching produk:", err);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchProduk();
  }, []);

  const { translated: products, loading: translating } = useTranslateItems(
    rawProducts,
    language,
    ["nama", "deskripsi"]
  );

  const handleAdd = (product) => {
    add(product);
    setAddedIds((prev) => [...prev, product.id]);
    setTimeout(() => setAddedIds((prev) => prev.filter((id) => id !== product.id)), 2000);
  };

  const isLoading = fetchLoading || translating;

  return (
    <section ref={ref} className="bg-white">

      {/* ── Hero Banner ── */}
      <div className="relative bg-[#1a4a35] overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-end pr-8 select-none pointer-events-none overflow-hidden">
          <span className="font-serif text-[180px] font-bold text-white/[0.04] leading-none">ETALASE</span>
        </div>
        <div
          className="absolute left-0 top-0 h-full w-1 bg-[#c9862a]"
          style={{ transform: loaded ? "scaleY(1)" : "scaleY(0)", transformOrigin: "top", transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s" }}
        />
        <div className="max-w-5xl mx-auto px-8 md:px-12 py-16 relative z-10">
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease 0.3s" }}
          >
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.localLabel}</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
                Etalase<br /><span className="text-[#c9862a]">{tx.heroTitle}</span>
              </h1>
              <p className="text-white/60 text-sm mt-4 max-w-md leading-relaxed">{tx.heroSubtitle}</p>
            </div>

            {/* Tombol-tombol hero — TIDAK ada modal di sini */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* View/Download Catalog button */}
              <button
                onClick={() => setCatalogOpen(true)}
                className="relative flex items-center gap-3 bg-[#c9862a] hover:bg-[#e09b3a] text-[#1a1a18] font-bold text-sm tracking-widest uppercase px-6 py-3.5 transition-colors duration-200 group w-fit flex-shrink-0"
              >
                <MdDownload size={18} />
                {tx.viewTrigger}
                <HiArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>

              {/* Cart button */}
              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center gap-3 border border-white/30 hover:border-[#c9862a] text-white hover:text-[#c9862a] font-bold text-sm tracking-widest uppercase px-6 py-3.5 transition-all duration-200 group w-fit flex-shrink-0"
              >
                <MdShoppingCart size={18} />
                {tx.cartTrigger}
                <HiArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                {count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#c9862a] text-[#1a1a18] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Product Grid ── */}
      <div className="max-w-5xl mx-auto px-8 md:px-12 py-14">
        <div
          className="flex items-center justify-between mb-10"
          style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(16px)", transition: "all 0.6s ease" }}
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.allProducts}</span>
            </div>
            <h2 className="font-serif text-2xl font-bold text-[#1a1a18]">{tx.gridTitle}</h2>
          </div>
          <div className="flex items-center gap-2 text-[#a09880] text-xs">
            <MdLocalOffer className="text-[#c9862a]" />
            <span>{rawProducts.length} {tx.allProducts.toLowerCase()}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-[#e8e0d0]" />
          <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
          <div className="flex-1 h-px bg-[#e8e0d0]" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            [1, 2, 3].map((i) => (
              <div key={i} className="bg-[#f7f3ec] border border-[#e8e0d0] animate-pulse" style={{ height: "420px" }} />
            ))
          ) : products.length === 0 ? (
            <div className="col-span-3 text-center py-20 text-[#a09880]">
              <FaSeedling className="text-5xl mx-auto mb-4 text-[#e8e0d0]" />
              <p className="text-sm">{tx.noProduct}</p>
            </div>
          ) : (
            products.map((product, i) => (
              <ProductCard
                key={product.id}
                product={product}
                index={i}
                inView={inView}
                onAdd={handleAdd}
                added={addedIds.includes(product.id)}
                tx={tx}
              />
            ))
          )}
        </div>

        <div
          className="mt-14 p-6 bg-[#f7f3ec] border border-[#e8e0d0] flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{ opacity: inView ? 1 : 0, transition: "opacity 0.8s ease 0.6s" }}
        >
          <FaSeedling className="text-[#1a4a35] text-2xl flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[#3a3a34] text-sm font-bold">{tx.noteTitle}</p>
            <p className="text-[#6b6456] text-xs mt-1 leading-relaxed">{tx.noteSub}</p>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2 border border-[#c9862a] text-[#1a1a18] text-xs font-bold tracking-widest uppercase px-5 py-2.5 hover:bg-[#c9862a] hover:text-white transition-all duration-200 group flex-shrink-0"
          >
            {tx.viewCart}
            <HiArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      {/* ── Flipbook Modal — DI LUAR flex row hero, sibling langsung <section> ── */}
      {catalogOpen && (
        <FlipbookModal onClose={() => setCatalogOpen(false)} tx={tx} />
      )}

      {/* ── Cart Drawer ── */}
      {cartOpen && (
        <CartDrawer
          cart={cart}
          onRemove={remove}
          total={total}
          onClose={() => setCartOpen(false)}
          tx={tx}
        />
      )}
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const EtalasePage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <EtalaseSection />
    <Footer />
  </main>
);

export default EtalasePage;